import { formatTimer, minesRemaining } from '@p4p2r0/minesweeper-core';
import type { GameState } from '@p4p2r0/minesweeper-core';
import React from 'react';

import { useI18n } from '../i18n';
import { Board } from './Board';
import { Face, type FaceMood } from './Face';
import { FlagIcon } from './Icons';

/** Three-digit counter, negative when you have over-flagged, as per the original. */
function ledCount(value: number): string {
  const clamped = Math.max(-99, Math.min(999, value));
  return clamped < 0
    ? `-${String(Math.abs(clamped)).padStart(2, '0')}`
    : String(clamped).padStart(3, '0');
}

interface GameConsoleProps {
  game: GameState;
  elapsedMs: number;
  mood: FaceMood;
  disabled: boolean;
  onReveal: (x: number, y: number) => void;
  onFlag: (x: number, y: number) => void;
  onChord: (x: number, y: number) => void;
  onPressChange: (pressing: boolean) => void;
  onNewBoard: () => void;
  /** A cell to draw attention to — used by the tutorial. */
  hintIndex?: number | null;
  /** See `Board`'s prop of the same name. */
  flagMode?: boolean;
  /** Omit to hide the toggle entirely (e.g. during the tutorial). */
  onFlagModeChange?: (flagMode: boolean) => void;
}

/**
 * The HUD + board, as one unit.
 *
 * Used for real play and for the tutorial alike, so a lesson runs on the same
 * console a real game does rather than a shrunken lookalike — what you
 * practice on is what you actually play on afterwards.
 */
export function GameConsole({
  game,
  elapsedMs,
  mood,
  disabled,
  onReveal,
  onFlag,
  onChord,
  onPressChange,
  onNewBoard,
  hintIndex = null,
  flagMode = false,
  onFlagModeChange,
}: GameConsoleProps): React.ReactElement {
  const { t } = useI18n();

  return (
    <div className="console">
      <div className="hud">
        <div className="hud-core">
          <div className="led" title={t('game.minesLeft')}>
            {ledCount(minesRemaining(game))}
          </div>
          <button type="button" className="face" aria-label={t('game.newBoard')} onClick={onNewBoard}>
            <Face mood={mood} />
          </button>
          <div className="led" title={t('game.elapsed')}>
            {formatTimer(elapsedMs)}
          </div>
        </div>
      </div>

      {/* A sibling of `.hud`, not a child — positioned off `.console`'s own
          corner (see CSS) so it floats clear above the mines/face/timer row
          instead of overlapping the timer LED, and never has to be balanced
          against anything for the row to stay flush-left/flush-right/centred. */}
      {onFlagModeChange ? (
        <button
          type="button"
          className={`flag-mode-toggle${flagMode ? ' active' : ''}`}
          aria-pressed={flagMode}
          title={t('game.flagModeHint')}
          onClick={() => onFlagModeChange(!flagMode)}
        >
          <FlagIcon />
        </button>
      ) : null}

      {/* Only a finished game blocks input: clicks made while the server is
          being talked to are queued by the session, not dropped. */}
      <Board
        state={game}
        disabled={disabled}
        onReveal={onReveal}
        onFlag={onFlag}
        onChord={onChord}
        onPressChange={onPressChange}
        hintIndex={hintIndex}
        flagMode={flagMode}
      />
    </div>
  );
}
